import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Button, ButtonText } from '@saucelabs/ds-button';
import { IconDevicePhoneFill, IconDeviceDesktopFill } from '@saucelabs/ds-icons';
import { Box, HStack, VStack } from '@saucelabs/sl-component-grid';
import { Typography } from '@saucelabs/ds-typography';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { ROUTE_PARENT_SECTION } from '../../constants';
import { Subscription } from '../../records/Subscription';
import { SubscriptionCheckbox } from './SubscriptionCheckbox';
import Styles from './SubscriptionCancelForm.styles';
import { trackEvent, EVENTS } from './../../tracking';
import { getProductType } from './../../records/Plan';

const validate = ({ subscriptions }) => (subscriptions?.length > 0 ? null : { [FORM_ERROR]: 'You need to select at least one subscription.' });

export const SubscriptionCancelForm = ({ subscriptions, displayModal }) => {
  const [selectedSub, setSelectedSub] = useState({});

  const submit = (values) => {
    const cancelledProductNames = subscriptions
      .filter((sub) => values.subscriptions.includes(sub.id))
      .map((sub) => getProductType(sub.plan) || 'legacy')
      .toArray();
    trackEvent(EVENTS.CANCEL_SUBMIT_FORM, { products: cancelledProductNames });
  };

  const SingleSelectionDescription = () => {
    const singleSub = Object.values(selectedSub)[0];
    return (
      <Typography variant="body">
        You will not be able to use
        {' '}
        <strong>
          { singleSub.displayName() }
        </strong>
        {' '}
        to find defects in your desktop/mobile browsers and apps after
        {' '}
        <strong>
          { singleSub.displayNextBillDate() }
        </strong>
        {''}
        .
      </Typography>
    );
  };

  const MultipleSelectionDescription = () => (
    <Typography variant="body">
      You will not be able to find defects in your web and mobile
      apps using a range of devices and browsers.
    </Typography>
  );

  return (
    <Box className="p-2">
      <Typography
        className="mb-1"
        variant="body"
      >
        Please select which plans you would like to cancel.
      </Typography>
      <Form
        onSubmit={submit}
        render={({ handleSubmit, submitting, error }) => {
          const isDisabled = (submitting || error);
          return (
            <form onSubmit={handleSubmit}>
              <VStack gap={1}>
                {subscriptions.valueSeq().map((sub) => (
                  <Field
                    key={sub.id}
                    name="subscriptions"
                    render={({ input }) => (
                      <SubscriptionCheckbox
                        checked={input.checked}
                        onChange={(event) => {
                          input.onChange(event);
                          const productName = getProductType(sub.plan) || 'legacy';
                          const isChecked = event.target.checked;
                          if (isChecked) {
                            const newSelections = {
                              ...selectedSub,
                              [sub.id]: sub,
                            };
                            setSelectedSub(newSelections);
                          } else {
                            delete selectedSub[sub.id];
                          }

                          trackEvent(
                            EVENTS.CANCEL_SELECTED_PRODUCT,
                            { [productName]: isChecked ? 'selected' : 'unselected' },
                          );
                        }}
                        subscription={sub}
                      />
                    )}
                    type="checkbox"
                    value={sub.id}
                  />
                ))}
              </VStack>
              { !isDisabled && (
                <div className="text-center mt-6">
                  <Typography
                    className="mb-2"
                    disableSpacing
                    variant="heading-3"
                  >
                    Canceling your plan today means:
                  </Typography>
                  <div className="mb-2 text-gray-600">
                    <IconDevicePhoneFill className="mr-2 fill-current" />
                    <IconDeviceDesktopFill className="fill-current" />
                  </div>
                  <div className="max-w-lg mx-auto">
                    {
                      (Object.keys(selectedSub).length === 1) ?
                        <SingleSelectionDescription /> :
                        <MultipleSelectionDescription />
                    }
                    <Typography variant="body">
                      Your account will remain in read-only mode after cancelation.
                    </Typography>
                  </div>
                </div>
              )}
              <div className={Styles.buttons}>
                <HStack
                  justifyContent="flex-end"
                >
                  <ButtonText
                    onPress={() => trackEvent(EVENTS.CANCEL_ABORT_FORM)}
                    to={`/${ROUTE_PARENT_SECTION}`}
                  >
                    { isDisabled ? 'Go Back' : 'Keep my Subscription' }
                  </ButtonText>
                  <Button
                    className="ml-2"
                    color="danger"
                    disabled={isDisabled}
                    onPress={() => displayModal(Object.keys(selectedSub))}
                  >
                    Unsubscribe now
                  </Button>
                </HStack>
              </div>
            </form>
          );
        }}
        validate={validate}
      />
    </Box>
  );
};

SubscriptionCancelForm.propTypes = {
  displayModal: PropTypes.func.isRequired,
  subscriptions: ImmutablePropTypes.listOf(PropTypes.instanceOf(Subscription)).isRequired,
};
